import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components';

export interface ColumnSettingsItem {
  key: string;
  title: string;
}

export interface ColumnSettingsProps {
  /** Customizable columns in their current order (visible + hidden). */
  items: ColumnSettingsItem[];
  /** Keys that are currently hidden. */
  hiddenKeys: string[];
  /** Commit a new order + hidden set (fired on Apply). */
  onApply: (order: string[], hiddenKeys: string[]) => void;
  /** Reset to the table's default layout. */
  onReset?: () => void;
  buttonText?: string;
  buttonIcon?: string;
  className?: string;
}

/**
 * Global "Edit Columns" control: a button that opens a popup where users pick
 * which columns to show and drag to reorder them. Pair it with the
 * `useTableColumns` hook, which owns the state + persistence and produces the
 * props for this component. See CLAUDE.md Rule 2 (shared components).
 */
export const ColumnSettings: React.FC<ColumnSettingsProps> = ({
  items,
  hiddenKeys,
  onApply,
  onReset,
  buttonText = 'Edit Columns',
  buttonIcon = 'ri-settings-3-line',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Draft state — only committed to the parent on Apply.
  const [draftOrder, setDraftOrder] = useState<string[]>([]);
  const [draftHidden, setDraftHidden] = useState<Set<string>>(new Set());
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);

  const titleByKey = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((item) => map.set(item.key, item.title));
    return map;
  }, [items]);

  // (Re)seed the draft whenever the popup opens or inputs change.
  useEffect(() => {
    if (isOpen) {
      setDraftOrder(items.map((item) => item.key));
      setDraftHidden(new Set(hiddenKeys));
    }
  }, [isOpen, items, hiddenKeys]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const visibleCount = draftOrder.filter((k) => !draftHidden.has(k)).length;
  const allSelected = visibleCount === draftOrder.length && draftOrder.length > 0;
  // Count of columns the user has hidden vs the table's baseline (all visible).
  const hiddenCount = hiddenKeys.length;

  const toggleColumn = (key: string) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all.
      setDraftHidden(new Set(draftOrder));
    } else {
      setDraftHidden(new Set());
    }
  };

  const moveKey = (from: string, to: string) => {
    if (from === to) return;
    setDraftOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(from);
      const toIdx = next.indexOf(to);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, from);
      return next;
    });
  };

  const handleApply = () => {
    onApply(draftOrder, Array.from(draftHidden));
    setIsOpen(false);
  };

  const handleClear = () => {
    // Match the back-office: "Clear" deselects every column.
    setDraftHidden(new Set(draftOrder));
  };

  const handleReset = () => {
    onReset?.();
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <Button
        variant="neutral"
        className="flex !border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50 border-[1px]"
        size="small"
        onClick={() => setIsOpen((open) => !open)}
      >
        <i
          className={`${buttonIcon} text-[12px] ${
            hiddenCount > 0 ? 'text-[#5e2aac]' : ''
          } mr-1`}
        ></i>
        {buttonText}
        {hiddenCount > 0 && (
          <span className="ml-1 bg-[#5e2aac] text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[16px] h-4 flex items-center justify-center">
            {hiddenCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div
          className="absolute right-0 z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg"
          role="dialog"
          aria-label="Edit columns"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2 text-[14px] font-semibold text-gray-900">
              <i className="ri-settings-3-line text-gray-500"></i>
              <span>Columns</span>
            </div>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-[13px] font-semibold text-[#5e2aac] hover:text-[#4a2088]"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Column list */}
          <div className="max-h-72 overflow-y-auto py-1">
            {draftOrder.map((key) => {
              const isHidden = draftHidden.has(key);
              const isDragging = dragKey === key;
              const isDropTarget = dropTargetKey === key && dragKey !== key;
              return (
                <div
                  key={key}
                  draggable
                  onDragStart={() => setDragKey(key)}
                  onDragEnd={() => {
                    setDragKey(null);
                    setDropTargetKey(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== key) setDropTargetKey(key);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragKey) moveKey(dragKey, key);
                    setDragKey(null);
                    setDropTargetKey(null);
                  }}
                  className={`group flex items-center gap-3 px-4 py-2 cursor-move select-none transition-colors ${
                    isDragging ? 'opacity-40' : ''
                  } ${
                    isDropTarget
                      ? 'border-t-2 border-[#5e2aac]'
                      : 'border-t-2 border-transparent'
                  } hover:bg-gray-50`}
                >
                  <label
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={!isHidden}
                      onChange={() => toggleColumn(key)}
                      className="h-4 w-4 rounded border-gray-300 text-[#5e2aac] focus:ring-[#5e2aac]"
                    />
                    <span
                      className={`text-[14px] truncate ${
                        isHidden ? 'text-gray-400' : 'text-gray-800'
                      }`}
                      title={titleByKey.get(key) || key}
                    >
                      {titleByKey.get(key) || key}
                    </span>
                  </label>
                  <i
                    className="ri-draggable text-gray-400 group-hover:text-gray-600 text-[16px] shrink-0"
                    aria-hidden="true"
                  ></i>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onReset ? handleReset : handleClear}
              className="text-[13px] font-medium text-gray-600 hover:text-gray-900"
            >
              {onReset ? 'Reset' : 'Clear'}
            </button>
            <div className="flex items-center gap-2">
              {onReset && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="rounded-md px-3 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100"
                >
                  Clear
                </button>
              )}
              <Button size="small" variant="primary" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSettings;
